/**
 * Prepares a product photo in the browser before upload (f4-06): a WebP of at most
 * 1 600 px for the panel, a 400 px WebP thumbnail, and a 1 600 px JPEG for WhatsApp,
 * which does not take WebP. Drawing it on a canvas drops EXIF (camera, GPS) and applies
 * the phone's rotation, so the photo stands the right way up.
 */

const FULL = 1600;
const THUMB = 400;
const WEBP_QUALITY = 0.8;
const JPEG_QUALITY = 0.85;
/** What a person may pick; it becomes much smaller. */
export const PHOTO_PICK_MAX = 25 * 1024 * 1024;

export interface PhotoFiles {
  full: Blob;
  thumb: Blob;
  wa: Blob;
}

async function draw(bitmap: ImageBitmap, max: number, type: "image/webp" | "image/jpeg", quality: number) {
  const scale = Math.min(1, max / Math.max(bitmap.width, bitmap.height));
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = new OffscreenCanvas(width, height);
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("no canvas");
  // JPEG has no transparency: a transparent PNG gets a white background, not black.
  if (type === "image/jpeg") {
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, width, height);
  }
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(bitmap, 0, 0, width, height);
  const blob = await canvas.convertToBlob({ type, quality });
  // A browser that cannot write WebP answers with PNG: not what the server accepts.
  if (blob.type !== type) throw new Error(`no ${type}`);
  return blob;
}

/** null when the browser cannot read the file as an image (for example, HEIC on some browsers). */
export async function preparePhoto(file: File): Promise<PhotoFiles | null> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    return null;
  }
  try {
    const [full, thumb, wa] = await Promise.all([
      draw(bitmap, FULL, "image/webp", WEBP_QUALITY),
      draw(bitmap, THUMB, "image/webp", WEBP_QUALITY),
      draw(bitmap, FULL, "image/jpeg", JPEG_QUALITY),
    ]);
    return { full, thumb, wa };
  } finally {
    bitmap.close();
  }
}
